import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getV3Account, FIXTURE_BADGE } from '@/services/v3.service.js';
import { getV3Communications, getV3DocumentsConnected } from '@/services/v3.service.js';
import { formatDateTime } from '@/lib/date-display.js';
import NextThing from '@/components/v3/NextThing.jsx';
import RenewalLead from '@/components/v3/RenewalLead.jsx';
import { FoldProvider } from '@/components/v3/TermBlock.jsx';
import { leadRenewal } from '@/components/v3/renewal-lead.js';
import AccountHeader from '@/components/v3/AccountHeader.jsx';
import OpenInClassic from '@/components/v3/OpenInClassic.jsx';
import TermsSection from '@/components/v3/TermsSection.jsx';
import AircraftSection from '@/components/v3/AircraftSection.jsx';
import PeopleSection from '@/components/v3/PeopleSection.jsx';
import InterestsSection from '@/components/v3/InterestsSection.jsx';
import DocumentsSection from '@/components/v3/DocumentsSection.jsx';
import DropZone from '@/components/v3/DropZone.jsx';
import CommunicationsSection from '@/components/v3/CommunicationsSection.jsx';
import PhoneLayout, { PHONE_QUERY } from '@/components/v3/PhoneLayout.jsx';
import useMediaQuery from '@/hooks/useMediaQuery.js';
import { indexItems } from '@/components/v3/words.js';
import { remedyHref } from '@/components/v3/doors.js';

// THE V3 ACCOUNT PAGE, /v3/account/:id (V3 overnight 1, Track C step 1).
// Home base for one account: the next-thing line on top, the account header,
// the current term open and older terms folded, aircraft, people, interests,
// documents, a drop zone, the conversation. Everything it shows is the payload
// of GET /api/v3/account/:id (docs/v3-account-api.md) and, for the
// conversation, of GET /api/v3/account/:id/communications (stage D3, read
// once the account is known and handed down as `conversation`); it derives
// no state word and no lifecycle, and every AIRgus item renders on the row
// of its fact_ref.
// Not linked from any nav. In a DEV build with the API off it renders the
// contract fixture under a visible badge. Under 640 px (useMediaQuery on
// PHONE_QUERY) the same payload renders as the phone column, PhoneLayout.

export default function V3Account() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phone = useMediaQuery(PHONE_QUERY);
  // EVERY READ CARRIES THE ID IT WAS READ FOR (2026-10-05). The page stays
  // mounted when the URL moves to another account (Cmd+K, a related account),
  // so the previous account's payload is still in state for a render. Read
  // untagged, the merge re-route below took that stale payload for a 303 and
  // put the OLD id back in the URL: every jump from one v3 account to another
  // bounced straight back. A payload, the conversation and the docs answer are
  // each used only for the id they were read for; anything else is loading.
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    let alive = true;
    setError(null);
    getV3Account(id)
      .then((r) => { if (alive) setRes({ ...r, for: id }); })
      .catch((e) => { if (alive) setError({ for: id, code: e?.code || e?.message || 'load failed' }); });
    return () => { alive = false; };
  }, [id]);
  useEffect(() => load(), [load]);

  const current = res && res.for === id ? res : null;
  const data = current?.data || null;
  const accountId = data?.account?.id || null;
  const [comms, setComms] = useState(null);
  const loadComms = useCallback(() => {
    if (!accountId) return undefined;
    let alive = true;
    setComms((c) => ({ for: accountId, data: c && c.for === accountId ? c.data : null, source: c && c.for === accountId ? c.source : null, error: null, loading: true }));
    getV3Communications(accountId)
      .then((r) => { if (alive) setComms({ for: accountId, data: r.data, source: r.source, error: null, loading: false }); })
      .catch((e) => { if (alive) setComms({ for: accountId, data: null, source: null, error: e?.code || e?.message || 'read failed', loading: false }); });
    return () => { alive = false; };
  }, [accountId]);
  useEffect(() => loadComms(), [loadComms]);
  const conversation = useMemo(() => ({ ...(comms && comms.for === accountId ? comms : { data: null, source: null, error: null, loading: !!accountId }), reload: loadComms }), [comms, loadComms, accountId]);
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    if (!accountId) return undefined;
    let alive = true;
    getV3DocumentsConnected(accountId)
      .then((r) => { if (alive) setDocs({ for: accountId, connected: r.connected }); })
      .catch(() => { if (alive) setDocs({ for: accountId, connected: null }); });   // a failed read toasted (api.js); the zone stays as it was
    return () => { alive = false; };
  }, [accountId]);
  const docsConnected = docs && docs.for === accountId ? docs.connected : null;
  useEffect(() => {
    // A merged account answered 303 and fetch followed it: the URL says one id,
    // the payload another. Put the survivor's id in the URL. Only a payload
    // read for THIS id may say so (above).
    if (!data || current?.source !== 'api') return;
    const a = data.account;
    if (a && a.id !== id && String(a.crm_account_id) !== String(id)) navigate(`/v3/account/${encodeURIComponent(a.id)}`, { replace: true });
  }, [data, current, id, navigate]);

  const { byRef, loose } = useMemo(() => indexItems(data?.airgus?.items), [data]);
  const ctx = useMemo(() => ({ account: data?.account, people: data?.people || [], aircraft: data?.aircraft || [], currentTermId: data?.current_term_id || null, peopleLinks: data?.people_links || null }), [data]);
  const href = useCallback((item) => remedyHref(item, { account: data?.account, terms: data?.terms || [] }), [data]);

  // Every state of the page carries the way back to classic, the failed and
  // loading ones most of all (lane C review C-R4): with no payload the table
  // answers from the URL id (guard: tests/v3-front-door.test.js 1e).
  const failed = error && error.for === id ? error.code : null;
  if (failed) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6" data-v3-account data-error={failed}>
        <p className="text-sm text-danger">Could not load this account: {failed}</p>
        <div className="mt-2 flex flex-wrap items-center gap-4">
          <button type="button" className="inline-flex min-h-[36px] items-center text-sm text-text-muted underline-offset-2 hover:underline" onClick={load}>Try again</button>
          <OpenInClassic payload={null} />
        </div>
      </div>
    );
  }
  if (!data) return <div className="mx-auto max-w-5xl px-4 py-6 text-sm italic text-text-dim" data-v3-account>Loading the account… <OpenInClassic payload={null} className="ml-3 not-italic" /></div>;
  if (data.ok === false) return <div className="mx-auto max-w-5xl px-4 py-6 text-sm text-danger" data-v3-account>The account read answered {data.error || 'an error'}. <OpenInClassic payload={null} className="ml-3" /></div>;

  const common = { byRef, ctx, onSaved: load, remedyHref: href };
  const badge = current.source === 'fixture' ? (
    <div className="flex items-center gap-2 rounded-card border border-warning/60 bg-warning-soft px-3 py-2 text-sm text-warning" data-fixture-badge>
      <span className="rounded-pill bg-warning px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">{FIXTURE_BADGE}</span>
      <span>The v3 API answered disabled or did not answer; this is the contract fixture, not a real account.</span>
    </div>
  ) : null;
  const readAt = <p className="text-[11px] text-text-dim">Read {data.read_at ? formatDateTime(data.read_at) : 'at an unrecorded time'}.</p>;
  const folding = !!leadRenewal(data);
  if (phone) {
    return (
      <FoldProvider value={folding}>
      <div data-v3-account data-source={current.source} data-layout="phone">
        <PhoneLayout docsConnected={docsConnected} data={data} badge={badge} loose={loose} focusTermId={searchParams.get('term')} common={common} conversation={conversation} />
        <div className="px-[16px] pb-4">{readAt}</div>
      </div>
      </FoldProvider>
    );
  }
  return (
    <FoldProvider value={folding}>
    <div className="mx-auto max-w-5xl space-y-4 px-4 py-4 pb-16" data-v3-account data-source={current.source} data-layout="desktop">
      {badge}
      <NextThing airgus={data.airgus} loose={loose} remedyHref={href} />
      <RenewalLead data={data} account={data.account} />
      <AccountHeader account={data.account} people={data.people || []} related={data.related_accounts || []} payload={data} {...common} />
      <TermsSection terms={data.terms || []} pending={data.pending_terms || []} currentTermId={data.current_term_id} focusTermId={searchParams.get('term')} people={data.people || []} conversation={conversation} {...common} />
      <AircraftSection aircraft={data.aircraft || []} {...common} />
      <PeopleSection people={data.people || []} {...common} />
      <InterestsSection interests={data.interests || []} byRef={byRef} remedyHref={href} />
      <DocumentsSection documents={data.documents || []} terms={data.terms || []} byRef={byRef} remedyHref={href} />
      <DropZone docsConnected={docsConnected} account={data.account} terms={data.terms || []} interests={data.interests || []} currentTermId={data.current_term_id || null} onSaved={load} />
      <CommunicationsSection conversation={conversation} terms={data.terms || []} />
      {readAt}
    </div>
    </FoldProvider>
  );
}
